import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import organizationClient from "@apis/organizationClient";
import { MenuLevelConst, useOrg } from "@/contexts/OrgContext";
import { RootState } from "@/types/RootState";

/**
 * The organisation type of whatever is currently in scope.
 *
 * - `"NORMAL"` / `"PARTNER"` / `"SYSTEM"` — that organisation's type
 * - `"ALL"` — cross level with nothing sliced; more than one org is in view
 * - `"UNKNOWN"` — the sliced org could not be resolved
 * - `null` — still resolving; render nothing rather than guessing
 */
export type ScopedOrgType = string | null;

/**
 * Resolves the type of the single organisation a page is looking at, so pages
 * whose objects only exist on one kind of organisation can render
 * `<NotAvailableAtThisLevel />` instead of a form that cannot be saved
 * (STIC-15790).
 *
 * Where the answer comes from:
 *
 * | scope | source |
 * |---|---|
 * | own level (no `?org=`) | the login info — fetching would need ORGANIZATION_VIEW, which a plain org user does not have |
 * | `?org=<id>` | fetched; system and partner cross-level logins do hold that permission |
 * | `?org=all` | `"ALL"` without a fetch — nothing is sliced |
 *
 * A failed fetch resolves to `"UNKNOWN"`, never back to `null`, so a page
 * guarding on this shows the explainer rather than staying blank forever.
 *
 * Typical use:
 *
 * ```tsx
 * const scopedOrgType = useScopedOrgType();
 * if (scopedOrgType === null) return null;
 * if (scopedOrgType !== "NORMAL") {
 *   return <NotAvailableAtThisLevel reason={NotAvailableReasonConst.ONLY_ON_NORMAL} />;
 * }
 * ```
 */
const useScopedOrgType = (): ScopedOrgType => {
  const { scopeOrgId, isAllOrgs, level } = useOrg();
  const ownOrg = useSelector((state: RootState) => state.user.organisation);
  const ownOrgType = ownOrg?.organisationType ?? null;
  // Own level is the login's own organisation, so its type is already in the
  // payload. Keyed on `level`, NOT on `scopeOrgId`: that is non-null at own level
  // too (it is the login's own org), so testing it sent every cross-level page
  // down the own-level branch and reported the LOGIN's type for whichever
  // organisation was selected - a partner browsing a customer org got "PARTNER"
  // and every `!== "NORMAL"` page answered "Not available at this level".
  const atOwnLevel = level === MenuLevelConst.OWN;

  const [scopedOrgType, setScopedOrgType] = useState<ScopedOrgType>(
    isAllOrgs ? "ALL" : atOwnLevel ? ownOrgType : null
  );

  useEffect(() => {
    if (isAllOrgs) {
      setScopedOrgType("ALL");
      return;
    }
    if (atOwnLevel) {
      setScopedOrgType(ownOrgType);
      return;
    }
    if (scopeOrgId == null) {
      setScopedOrgType(null);
      return;
    }
    setScopedOrgType(null);
    organizationClient.getBasicOrgWithId(scopeOrgId).then(
      ({ data }: { data?: { organisationType?: string } }) =>
        setScopedOrgType(data?.organisationType ?? "UNKNOWN"),
      () => setScopedOrgType("UNKNOWN")
    );
  }, [scopeOrgId, isAllOrgs, atOwnLevel, ownOrgType]);

  return scopedOrgType;
};

export default useScopedOrgType;
